/**
 * The Console password policy (IP-79): ONE module, imported by the browser
 * (the reset and change-password forms) and by the Console API (before it
 * asks Cognito), so the two can never disagree about what a password must be.
 *
 * The first five rules are the user pool's own (36-console-identity.yaml:
 * MinimumLength 12, RequireUppercase, RequireLowercase, RequireNumbers,
 * RequireSymbols), stated here so a person learns what is wrong before
 * Cognito refuses it with InvalidPasswordException. The rest are stricter than
 * the pool and are what "validate the password strength" means beyond the
 * pool's floor: no surrounding spaces (Cognito refuses them with a message
 * nobody can act on), not built from the person's own email or name, not a
 * run of one character, and not the password being replaced.
 *
 * PURE and browser-safe: no Node module, no network.
 */

export const MIN_LENGTH = 12;
export const MAX_LENGTH = 128;

/**
 * The characters Cognito counts as symbols (its RequireSymbols list), plus the
 * space it accepts inside a password.
 */
export const SYMBOLS = '^$*.[]{}()?"!@#%&/\\,><\':;|_~`=+- ';

const SYMBOL_SET = new Set(SYMBOLS);

/** The rule codes, in the order a form lists them. */
export const RULES = Object.freeze([
  { code: 'LENGTH', text: MIN_LENGTH + ' to ' + MAX_LENGTH + ' characters' },
  { code: 'UPPER', text: 'An upper-case letter' },
  { code: 'LOWER', text: 'A lower-case letter' },
  { code: 'NUMBER', text: 'A number' },
  { code: 'SYMBOL', text: 'A symbol, such as ! @ # $ % & *' },
  { code: 'NO_EDGE_SPACE', text: 'No space at the start or the end' },
  { code: 'NOT_PERSONAL', text: 'Not your email address or name' },
  { code: 'NOT_REPEATED', text: 'Not one character over and over' },
]);

const MESSAGES = Object.freeze({
  LENGTH: 'Use ' + MIN_LENGTH + ' to ' + MAX_LENGTH + ' characters.',
  UPPER: 'Include an upper-case letter.',
  LOWER: 'Include a lower-case letter.',
  NUMBER: 'Include a number.',
  SYMBOL: 'Include a symbol, such as ! @ # $ % & *.',
  NO_EDGE_SPACE: 'Do not start or end the password with a space.',
  NOT_PERSONAL: 'Do not build the password from your email address or name.',
  NOT_REPEATED: 'Do not repeat one character over and over.',
  SAME_AS_CURRENT: 'Choose a password different from your current one.',
  MISMATCH: 'The two passwords do not match.',
});

/** The pieces of a person's identity a password must not be built from (3+ characters each). */
function personalFragments({ email, firstName, lastName } = {}) {
  const out = [];
  const local = typeof email === 'string' ? email.split('@')[0] : '';
  for (const piece of [local, ...local.split(/[._+-]/), firstName, lastName]) {
    const text = typeof piece === 'string' ? piece.trim().toLowerCase() : '';
    if (text.length >= 3 && !out.includes(text)) out.push(text);
  }
  return out;
}

/**
 * Each rule, and whether the password meets it - what the form's checklist
 * shows as the person types.
 *
 * @param {string} password
 * @param {{ email?: string, firstName?: string, lastName?: string }} [person]
 * @returns {Array<{ code: string, text: string, met: boolean }>}
 */
export function checkRules(password, person = {}) {
  const value = typeof password === 'string' ? password : '';
  const chars = [...value];
  const lower = value.toLowerCase();
  const met = {
    LENGTH: chars.length >= MIN_LENGTH && chars.length <= MAX_LENGTH,
    UPPER: /[A-Z]/.test(value),
    LOWER: /[a-z]/.test(value),
    NUMBER: /[0-9]/.test(value),
    // Cognito's list only: a character it does not count (é, £) would pass
    // here and still be refused there.
    SYMBOL: chars.some((c) => SYMBOL_SET.has(c) && c !== ' '),
    NO_EDGE_SPACE: value === value.trim(),
    NOT_PERSONAL: !personalFragments(person).some((fragment) => lower.includes(fragment)),
    NOT_REPEATED: value.length === 0 || new Set(chars).size > 2,
  };
  return RULES.map((rule) => ({ ...rule, met: met[rule.code] }));
}

/**
 * Everything wrong with a proposed password, as sentences a person can act
 * on, in the checklist's order; [] when it is acceptable.
 *
 * @param {string} password
 * @param {{ email?: string, firstName?: string, lastName?: string, current?: string, confirm?: string }} [context]
 *   current - the password being replaced (a change); confirm - the second entry, when the form has one
 * @returns {Array<{ code: string, message: string }>}
 */
export function passwordIssues(password, context = {}) {
  const issues = checkRules(password, context).filter((rule) => !rule.met).map((rule) => ({ code: rule.code, message: MESSAGES[rule.code] }));
  if (typeof context.current === 'string' && context.current !== '' && password === context.current) {
    issues.push({ code: 'SAME_AS_CURRENT', message: MESSAGES.SAME_AS_CURRENT });
  }
  if (Object.prototype.hasOwnProperty.call(context, 'confirm') && context.confirm !== password) {
    issues.push({ code: 'MISMATCH', message: MESSAGES.MISMATCH });
  }
  return issues;
}

/** The first issue's sentence, or null - the shape a form validator wants. */
export function firstPasswordIssue(password, context = {}) {
  return passwordIssues(password, context)[0]?.message ?? null;
}

const SEQUENCES = ['abcdefghijklmnopqrstuvwxyz', '0123456789', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

/** How many characters sit in runs of 3+ ascending or keyboard-adjacent characters. */
function sequentialChars(lower) {
  let count = 0;
  for (let i = 0; i + 2 < lower.length; i += 1) {
    const triple = lower.slice(i, i + 3);
    if (SEQUENCES.some((seq) => seq.includes(triple) || [...seq].reverse().join('').includes(triple))) count += 1;
  }
  return count;
}

/**
 * A 0..4 strength estimate for the meter: 0 very weak, 4 strong. It is a
 * guide, not a rule - a password that fails passwordIssues() scores at most 1,
 * whatever its length, so the meter never calls a refused password strong.
 *
 * @returns {{ score: 0|1|2|3|4, label: string }}
 */
export function passwordStrength(password, person = {}) {
  const value = typeof password === 'string' ? password : '';
  if (value === '') return { score: 0, label: 'Too short' };
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(value)).length;
  const unique = new Set(value).size;
  let points = Math.min(value.length, 24) / 4 + classes + Math.min(unique, 16) / 4;
  points -= sequentialChars(value.toLowerCase()) * 0.75;
  let score;
  if (points < 5) score = 0;
  else if (points < 8) score = 1;
  else if (points < 10.5) score = 2;
  else if (points < 13) score = 3;
  else score = 4;
  if (passwordIssues(value, person).length) score = Math.min(score, 1);
  return { score, label: ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'][score] };
}
